import React from "react";
import { useTranslation } from "react-i18next";
import { useGlobal } from "reactn";
import { Item } from "@outplayed/tooltips";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { stackDuplicateItems } from "@ugg/shared/utils/item-helpers";
import { ReactComponent as ArrowRight } from "@ugg/shared/assets/svg/arrow-right.svg";
import { OverviewInfo, ItemOption } from "@ugg/shared/api/data-parser/champions/overview";

// ─────────────────────────────────────────────────────────────────────
// Items — ARAM Mayhem's own copy, a shallow band instead of five tall columns.
//
// A deliberate sibling of Overview/ItemsRoleQuest.tsx rather than a reuse of
// it. Same two reasons as AramMayhemSkills (no stats, no
// `.champion-recommended-build` stylesheet scope), plus a third that is really
// a consequence of the first:
//
//   Take the win rates out of ItemsRoleQuest and the cells empty but keep
//   their height. `.starting-items`, `.core-items` and `.item-options-1` are
//   flex columns whose `.content-section_blurb` carries
//   `flex: 1; align-items: flex-end`, so the blurb pins to the bottom of
//   whatever the tallest column sets — and with no stats that tallest column
//   is three stacked 36px icons over a lot of dead space. So the layout here
//   is different in kind, not just in what it hides: item options run
//   horizontally instead of stacking, icon groups left-align under their
//   headers, and the "Best for most matchups" style blurbs are gone.
//
// ARAM's role is "none", so none of ItemsRoleQuest's role branches applied
// anyway: the seventh-item column and the ADC gutter are gated on
// role === "adc", the tier-3 boots quest row on role === "mid". That is why
// this takes no `role` and renders exactly five columns.
// ─────────────────────────────────────────────────────────────────────

const ITEM_SIZE = 36;

// Card surface for each cell. The 1px grid gap over the near-black track
// below draws the dividers, so no cell carries a border.
const CELL = "flex flex-col bg-[#191937] p-[12px] xxs:p-[18px]";

// `Item` with `isSprite` sizes its own inner box; this class lands on the
// wrapper and supplies what `.item-img` did — rounding and a clip. The 36px
// restates ITEM_SIZE as a literal on purpose: Tailwind's content scanner reads
// source text, so an interpolated class would emit no rule.
const ITEM_IMG = "h-[36px] w-[36px] overflow-hidden rounded-[3px]";

// The overview feed uses 0 as "no item here" — a champion with thin ARAM data
// can come back as `[0]` or with a 0 padding out a short list. 0 is not an item
// id, and `Item` renders it as a broken sprite rather than nothing, so strip
// them before anything counts or stacks. (`displayItems` does the equivalent
// check for the build pages; this module doesn't go through it.)
function validItemIds(ids: number[] | undefined | null): number[] {
  return (ids || []).filter((id) => id > 0);
}

interface AramMayhemItemsProps {
  startingItems: OverviewInfo["rec_starting_items"];
  coreItems: OverviewInfo["rec_core_items"];
  items1: OverviewInfo["item_options_1"];
  items2: OverviewInfo["item_options_2"];
  items3: OverviewInfo["item_options_3"];
  patch: string;
}

export function AramMayhemItems(props: AramMayhemItemsProps) {
  const { t } = useTranslation();
  const { startingItems, coreItems, items1, items2, items3, patch } = props;

  // Starting items legitimately repeat (two Health Potions), so they stack
  // into one icon with a count badge rather than rendering twice.
  const dupeStartingItems = stackDuplicateItems(validItemIds(startingItems?.ids));

  return (
    // Five across at 1140px and up, folding down rather than squashing:
    // three per row from 900px, two from 600px, one below that.
    <div className="grid grid-cols-1 gap-[1px] overflow-hidden rounded-[3px] bg-[#070720] xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
      <div className={CELL}>
        <HeaderBullet className="mb-[18px] whitespace-nowrap">{t("Starting Items")}</HeaderBullet>
        <div className="flex items-center gap-[6px]">
          {dupeStartingItems.map((item, index) => (
            <ItemDupe key={index} itemId={item.id} count={item.count} patch={patch} />
          ))}
        </div>
      </div>
      <div className={CELL}>
        <HeaderBullet className="mb-[18px] whitespace-nowrap">{t("Core Items")}</HeaderBullet>
        <CoreItemsPath ids={coreItems?.ids} patch={patch} />
      </div>
      <ItemOptionsCell label={t("Fourth Item Options")} items={items1} patch={patch} />
      <ItemOptionsCell label={t("Fifth Item Options")} items={items2} patch={patch} />
      <ItemOptionsCell label={t("Sixth Item Options")} items={items3} patch={patch} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────

function ItemDupe({ itemId, count, patch }: { itemId: number; count: number; patch: string }) {
  const [language] = useGlobal("language");

  return (
    <div className="relative">
      <Item className={ITEM_IMG} itemId={itemId} spriteSize={ITEM_SIZE} isSprite patch={patch} ssr language={language} webp />
      {count > 1 && (
        // Badge overhangs the icon's bottom-right corner, same as
        // `.item-dupe .count-label` on the build pages. pointer-events-none so
        // it doesn't punch a hole in the item's tooltip target.
        <div className="pointer-events-none absolute right-0 bottom-0 flex h-[16px] w-[16px] translate-x-[30%] translate-y-[30%] items-center justify-center rounded-full bg-[#383864] text-[11px] font-bold text-white">
          {count}
        </div>
      )}
    </div>
  );
}

// Core build order, arrows between the steps. Written out rather than going
// through `displayItems`, whose markup hangs on `.image-wrapper` / `.sprite` /
// `.path-arrow` — classes that are only styled inside
// `.champion-recommended-build`.
function CoreItemsPath({ ids, patch }: { ids: number[] | undefined | null; patch: string }) {
  const items = stackDuplicateItems(validItemIds(ids));
  if (items.length === 0) return null;

  return (
    <div className="flex items-center gap-[8px]">
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <ItemDupe itemId={item.id} count={item.count} patch={patch} />
          {index !== items.length - 1 && <ArrowRight className="w-[12px] flex-none" />}
        </React.Fragment>
      ))}
    </div>
  );
}

// One "Nth Item Options" column: a wrapping row of icons. The stat text that
// used to sit beside each icon is what made these columns tall, so with it
// gone they run horizontally and the whole module is one band deep.
function ItemOptionsCell({ label, items, patch }: { label: string; items: ItemOption[] | null; patch: string }) {
  const [language] = useGlobal("language");

  return (
    <div className={CELL}>
      <HeaderBullet className="mb-[18px] whitespace-nowrap">{label}</HeaderBullet>
      <div className="flex flex-wrap items-center gap-[12px]">
        {(items || [])
          .filter((item) => item.id > 0)
          .map((item, index) => (
            <Item
              key={index}
              className={ITEM_IMG}
              itemId={item.id}
              spriteSize={ITEM_SIZE}
              isSprite
              patch={patch}
              ssr
              language={language}
              webp
            />
          ))}
      </div>
    </div>
  );
}
